import { useMemo } from "react";
import PageHeader from "components/PageHeader";
import useTabParam from "components/hooks/useTabParam";
import { downloadCsv, extractApiErrorMessage } from "utils/util";
import DSAlert from "views/system-design/DSAlert";
import DSBox from "views/system-design/DSBox";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import PaymentsRangeFilter, {
  rangeLabel,
} from "views/Dashboard/components/PaymentsRangeFilter";
import useRangeParams from "../hooks/useRangeParams";
import {
  useRefreshRetailers,
  useRetailerList,
  useRetailerStats,
} from "../hooks/useRetailers";
import {
  STATS_CSV_HEADERS,
  buildChartRows,
  buildStatsCsvRows,
} from "../utils/retailerChart";
import RetailerClocksChartCard from "./RetailerClocksChartCard";
import RetailerDetail from "./RetailerDetail";
import RetailerSelect from "./RetailerSelect";

/**
 * The clocks we sent to our retailers and how many were sold. Which companies
 * are retailers is set in Shopify (company metafield custom.retailer_type), not
 * here. The selected retailer and the period live in the URL (?retailer=<id>,
 * ?from=&to=) so a view can be shared.
 */
const Retailers = () => {
  // Both bounds absent is "all time", the default. Kept in the URL with the retailer.
  const [range, setRange] = useRangeParams();
  const [retailerId, setRetailerId] = useTabParam("retailer");

  const list = useRetailerList();
  const stats = useRetailerStats(range);
  const refresh = useRefreshRetailers();

  const rows = useMemo(() => buildChartRows(stats.data ?? []), [stats.data]);

  const exportCsv = () =>
    downloadCsv({
      filename: `retailers_${rangeLabel(range)}.csv`,
      headers: STATS_CSV_HEADERS,
      rows: buildStatsCsvRows(stats.data ?? []),
    });

  return (
    <DSBox>
      <PageHeader
        overline="Commerce"
        title="Retailers"
        subtitle="Clocks we sent to our retailers and how many were sold."
        actions={
          <DSStack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <PaymentsRangeFilter initialRange={range} onChange={setRange} />
            <DSSecondaryButton
              size="small"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Refresh data
            </DSSecondaryButton>
            <DSSecondaryButton
              size="small"
              disabled={!stats.data?.length}
              onClick={exportCsv}
            >
              Export CSV
            </DSSecondaryButton>
          </DSStack>
        }
      />

      {refresh.isError && (
        <DSAlert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(
            refresh.error,
            "Could not refresh the data from Shopify and BigQuery",
          )}
        </DSAlert>
      )}

      <RetailerClocksChartCard
        rows={rows}
        isLoading={stats.isLoading}
        errorMessage={
          stats.isError && !stats.data
            ? extractApiErrorMessage(
                stats.error,
                "Could not load the retailers",
              )
            : undefined
        }
        onRetry={() => stats.refetch()}
        selectedId={retailerId}
        onSelect={setRetailerId}
      />

      <DSBox sx={{ mt: 3 }}>
        <RetailerSelect
          retailers={list.data ?? []}
          isLoading={list.isLoading}
          value={retailerId}
          onChange={setRetailerId}
        />
      </DSBox>

      {retailerId ? (
        <RetailerDetail
          id={retailerId}
          range={range}
          onClear={() => setRetailerId(undefined)}
        />
      ) : (
        <DSTypography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Pick a retailer, or click a bar above, to see its details.
        </DSTypography>
      )}
    </DSBox>
  );
};

export default Retailers;
